// src/pages/EmployeeAuth.jsx
//
// Экран входа сотрудника. Три поля:
//   1. ID магазина (8 символов)
//   2. Логин или PIN
//   3. Пароль (опционально — если магазин в PIN-режиме, можно оставить пустым)
//
// Логика: бэк сам разбирает режим магазина по shopId и проверяет credentials.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { errorMessage } from '../utils/errorMessage';
import Icon from '../components/Icon';
import LanguageSelect from '../components/LanguageSelect';
import PasswordInput from '../components/PasswordInput';

export default function EmployeeAuth({ onBack }) {
  const { t } = useTranslation();
  const { loginEmployee } = useAuth();

  const [shopId, setShopId] = useState('');
  const [credential, setCredential] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (!shopId.trim()) return setError(t('employeeAuth.errorShopRequired'));
    if (!credential.trim()) return setError(t('employeeAuth.errorCredentialRequired'));

    setLoading(true);
    try {
      await loginEmployee(shopId.trim().toUpperCase(), credential.trim(), password || null);
      // После успешного входа useAuth ставит role='employee', mode='shop',
      // App автоматически рендерит ShopHome — никаких ручных navigate не нужно.
    } catch (e) {
      setError(errorMessage(e, t));
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      padding: 24,
      background: 'var(--bg)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <button
          onClick={onBack}
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--bg-elevated)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--fg)',
          }}
        >
          <Icon name="chevronLeft" size={18} />
        </button>
        <LanguageSelect />
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, marginBottom: 8,
          letterSpacing: '-0.02em', fontFamily: 'var(--font-display)',
        }}>
          {t('employeeAuth.title')}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginBottom: 28 }}>
          {t('employeeAuth.subtitle')}
        </p>

        <div className="input-group">
          <label>{t('employeeAuth.shopId')}</label>
          <input
            className="input"
            value={shopId}
            onChange={e => setShopId(e.target.value)}
            placeholder="ABCD1234"
            autoCapitalize="characters"
            autoComplete="off"
            maxLength={8}
            style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}
          />
        </div>

        <div className="input-group">
          <label>{t('employeeAuth.credential')}</label>
          <input
            className="input"
            value={credential}
            onChange={e => setCredential(e.target.value)}
            placeholder={t('employeeAuth.credentialPlaceholder')}
            autoCapitalize="none"
            autoComplete="off"
          />
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
            {t('employeeAuth.credentialHint')}
          </p>
        </div>

        <div className="input-group">
          <label>{t('employeeAuth.password')}</label>
          <PasswordInput
            className="input"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('employeeAuth.passwordPlaceholder')}
            autoComplete="current-password"
          />
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
            {t('employeeAuth.passwordHint')}
          </p>
        </div>

        {error && (
          <div style={{
            padding: 12, marginTop: 8, marginBottom: 8,
            background: 'var(--danger-soft)', color: 'var(--danger)',
            borderRadius: 'var(--r-sm)', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <button
          className="btn btn-primary btn-lg"
          onClick={handleSubmit}
          disabled={loading}
          style={{ width: '100%', marginTop: 16 }}
        >
          {loading ? '…' : t('employeeAuth.submit')}
        </button>
      </div>
    </div>
  );
}
